<template>
  <v-dialog
    persistent
    v-model="isOpen"
    max-width="95vw"
    @click:outside="closeDialog"
    @keydown.esc="closeDialog"
    @keydown.left="imagenAnterior"
    @keydown.right="imagenSiguiente"
  >
    <v-card class="elevation-0">
      <v-card-title class="pb-0">
        <span v-if="totalImagenes > 1" class="text-caption grey--text">
          {{ indiceActual + 1 }} / {{ totalImagenes }}
        </span>
        <v-spacer></v-spacer>
        <template v-if="!esVideo">
          <v-btn icon small title="Alejar" :disabled="!zoomActivo" @click="zoomOut()">
            <v-icon>mdi-magnify-minus</v-icon>
          </v-btn>
          <v-slider
            v-model="zoomSlider"
            class="zoom-slider mx-1"
            :min="100"
            :max="800"
            :step="10"
            hide-details
            dense
            title="Ajustar zoom"
            @keydown.native.left.stop
            @keydown.native.right.stop
          ></v-slider>
          <v-btn icon small title="Acercar" :disabled="zoomScale >= ZOOM_MAX" @click="zoomIn()">
            <v-icon>mdi-magnify-plus</v-icon>
          </v-btn>
          <span class="text-caption grey--text mx-1 zoom-porcentaje">{{ zoomPorcentaje }}%</span>
          <v-btn icon small title="Descargar" @click="descargarImagenActual">
            <v-icon>mdi-download</v-icon>
          </v-btn>
        </template>
        <v-btn fab small text @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-2 imagen-viewer-wrapper">
        <v-btn
          v-if="puedeAnterior"
          icon
          large
          class="nav-btn nav-btn-left"
          title="Imagen anterior"
          @click="imagenAnterior"
        >
          <v-icon large>mdi-chevron-left</v-icon>
        </v-btn>

        <div
          v-if="!esVideo"
          ref="contenedorZoom"
          class="imagen-contenedor"
          :class="{ 'imagen-contenedor--zoom': zoomActivo }"
          @wheel.prevent="onWheelZoom"
          @mousedown="iniciarArrastre"
        >
          <img
            :src="imagenSrc"
            ref="imagenZoom"
            class="imagen-viewer"
            :class="{ 'imagen-viewer--zoom': zoomActivo }"
            :style="imagenZoomStyle"
            draggable="false"
            @click="onClickImagen"
            @contextmenu.prevent="descargarImagenActual"
          />
        </div>

        <video
          v-else
          id="video"
          :src="imagenSrc"
          frameborder="0"
          allowfullscreen
          controls
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          class="video-viewer"
          allowtransparency="true"
        ></video>

        <v-btn
          v-if="puedeSiguiente"
          icon
          large
          class="nav-btn nav-btn-right"
          title="Imagen siguiente"
          @click="imagenSiguiente"
        >
          <v-icon large>mdi-chevron-right</v-icon>
        </v-btn>
      </v-card-text>

      <v-card-text v-if="!esVideo" class="pt-0 text-center text-caption grey--text">
        Clic para acercar 10% en el punto elegido · Mantén el clic y arrastra para mover · Rueda, slider o +/- para zoom · Clic derecho para descargar · Flechas para navegar
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { getEvidenciaBaseUrl, descargarEvidencia } from '@/utils/ticketEvidencias'

const VIDEO_EXTENSIONS = ['mp4', 'mov', 'wmv', 'avi']
const ZOOM_STEP = 0.1
const ZOOM_WHEEL_STEP = 0.1
const ZOOM_MIN = 1
const ZOOM_MAX = 8

export default {
  props: {
    isOpen: Boolean,
    ticket: Object,
    imagen: String,
    respuestaImagen: [Boolean, Number],
    imagenes: {
      type: Array,
      default: () => [],
    },
    indiceInicial: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      ZOOM_MAX,
      indiceActual: 0,
      zoomScale: 1,
      // Desplazamiento al hacer zoom (estilo WhatsApp: scale + translate)
      panX: 0,
      panY: 0,
      video: false,
      arrastre: {
        activo: false,
        movido: false,
        startX: 0,
        startY: 0,
        panX0: 0,
        panY0: 0,
      },
    }
  },

  computed: {
    listaImagenes() {
      if (this.imagenes && this.imagenes.length) {
        return this.imagenes
      }
      if (this.imagen) {
        return [{ foto: this.imagen, es_solucion: this.respuestaImagen }]
      }
      return []
    },

    itemActual() {
      return this.listaImagenes[this.indiceActual] || {}
    },

    imagenActual() {
      return this.itemActual.foto || this.imagen || ''
    },

    esRespuestaImagen() {
      if (this.itemActual.es_solucion !== undefined) {
        return this.itemActual.es_solucion
      }
      return this.respuestaImagen
    },

    imagenSrc() {
      return getEvidenciaBaseUrl(this.ticket, this.esRespuestaImagen) + this.imagenActual
    },

    totalImagenes() {
      return this.listaImagenes.length
    },

    puedeAnterior() {
      return this.indiceActual > 0
    },

    puedeSiguiente() {
      return this.indiceActual < this.totalImagenes - 1
    },

    esVideo() {
      return this.video
    },

    zoomActivo() {
      return this.zoomScale > 1.001
    },

    zoomPorcentaje() {
      return Math.round(this.zoomScale * 100)
    },

    zoomSlider: {
      get() {
        return Math.round(this.zoomScale * 100)
      },
      set(valor) {
        this.aplicarZoom(valor / 100)
      },
    },

    imagenZoomStyle() {
      return {
        transform: `translate(${this.panX}px, ${this.panY}px) scale(${this.zoomScale})`,
        transformOrigin: 'center center',
      }
    },
  },

  watch: {
    isOpen(val) {
      if (val) {
        this.indiceActual = this.indiceInicial
        this.resetZoom()
        this.detectarVideo()
      } else {
        this.resetZoom()
        this.video = false
      }
    },

    indiceActual() {
      this.resetZoom()
      this.detectarVideo()
    },
  },

  methods: {
    detectarVideo() {
      const nombreSplit = (this.imagenActual || '').split('.')
      const extension = nombreSplit[nombreSplit.length - 1].toLowerCase()
      this.video = VIDEO_EXTENSIONS.includes(extension)
    },

    resetZoom() {
      this.zoomScale = 1
      this.panX = 0
      this.panY = 0
    },

    // Zoom hacia el punto del cursor (como WhatsApp): el pixel bajo el clic se queda fijo
    aplicarZoom(escalaNueva, e) {
      const cont = this.$refs.contenedorZoom
      const escalaVieja = this.zoomScale
      const escala = Math.max(ZOOM_MIN, Math.min(escalaNueva, ZOOM_MAX))

      if (!cont || !e || escala === escalaVieja) {
        this.zoomScale = escala
        if (escala <= ZOOM_MIN) {
          this.panX = 0
          this.panY = 0
        }
        return
      }

      const rect = cont.getBoundingClientRect()
      // Punto del clic relativo al centro del contenedor
      const cx = e.clientX - rect.left - rect.width / 2
      const cy = e.clientY - rect.top - rect.height / 2

      // Mantener el mismo punto de la imagen bajo el cursor al cambiar scale
      const ratio = escala / escalaVieja
      this.panX = cx - (cx - this.panX) * ratio
      this.panY = cy - (cy - this.panY) * ratio
      this.zoomScale = escala

      if (escala <= ZOOM_MIN) {
        this.panX = 0
        this.panY = 0
      } else {
        this.limitarPan()
      }
    },

    limitarPan() {
      const cont = this.$refs.contenedorZoom
      const img = this.$refs.imagenZoom
      if (!cont || !img || this.zoomScale <= 1) {
        this.panX = 0
        this.panY = 0
        return
      }

      const baseW = img.offsetWidth || img.clientWidth || 0
      const baseH = img.offsetHeight || img.clientHeight || 0
      const scaledW = baseW * this.zoomScale
      const scaledH = baseH * this.zoomScale
      const maxX = Math.max(0, (scaledW - cont.clientWidth) / 2)
      const maxY = Math.max(0, (scaledH - cont.clientHeight) / 2)

      this.panX = Math.max(-maxX, Math.min(maxX, this.panX))
      this.panY = Math.max(-maxY, Math.min(maxY, this.panY))
    },

    toggleZoom(e) {
      if (this.zoomScale >= ZOOM_MAX) {
        this.resetZoom()
      } else {
        this.aplicarZoom(this.zoomScale + ZOOM_STEP, e)
      }
    },

    zoomIn(punto) {
      if (this.zoomScale < ZOOM_MAX) {
        this.aplicarZoom(this.zoomScale + ZOOM_STEP, punto || this.puntoCentro())
      }
    },

    zoomOut(punto) {
      if (this.zoomActivo) {
        this.aplicarZoom(this.zoomScale - ZOOM_STEP, punto || this.puntoCentro())
      }
    },

    puntoCentro() {
      const cont = this.$refs.contenedorZoom
      if (!cont) return null
      const rect = cont.getBoundingClientRect()
      return {
        clientX: rect.left + rect.width / 2,
        clientY: rect.top + rect.height / 2,
      }
    },

    onWheelZoom(e) {
      const punto = { clientX: e.clientX, clientY: e.clientY }
      if (e.deltaY < 0) {
        this.aplicarZoom(this.zoomScale + ZOOM_WHEEL_STEP, punto)
      } else if (this.zoomActivo) {
        this.aplicarZoom(this.zoomScale - ZOOM_WHEEL_STEP, punto)
      }
    },

    iniciarArrastre(e) {
      if (!this.zoomActivo || e.button !== 0) return

      this.arrastre.activo = true
      this.arrastre.movido = false
      this.arrastre.startX = e.clientX
      this.arrastre.startY = e.clientY
      this.arrastre.panX0 = this.panX
      this.arrastre.panY0 = this.panY

      window.addEventListener('mousemove', this.moverArrastre)
      window.addEventListener('mouseup', this.terminarArrastre)
    },

    moverArrastre(e) {
      if (!this.arrastre.activo) return
      const dx = e.clientX - this.arrastre.startX
      const dy = e.clientY - this.arrastre.startY

      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
        this.arrastre.movido = true
      }

      this.panX = this.arrastre.panX0 + dx
      this.panY = this.arrastre.panY0 + dy
      this.limitarPan()
    },

    terminarArrastre() {
      this.arrastre.activo = false
      window.removeEventListener('mousemove', this.moverArrastre)
      window.removeEventListener('mouseup', this.terminarArrastre)
    },

    onClickImagen(e) {
      if (this.arrastre.movido) {
        this.arrastre.movido = false
        return
      }
      this.toggleZoom(e)
    },

    imagenAnterior() {
      if (this.puedeAnterior) {
        this.indiceActual -= 1
      }
    },

    imagenSiguiente() {
      if (this.puedeSiguiente) {
        this.indiceActual += 1
      }
    },

    async descargarImagenActual() {
      if (this.esVideo || !this.imagenActual) return
      await descargarEvidencia(this.ticket, this.imagenActual, this.esRespuestaImagen)
    },

    closeDialog() {
      this.$emit('close')
    },
  },

  beforeDestroy() {
    window.removeEventListener('mousemove', this.moverArrastre)
    window.removeEventListener('mouseup', this.terminarArrastre)
  },
}
</script>

<style scoped>
.imagen-viewer-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.imagen-contenedor {
  width: 100%;
  height: 80vh;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.imagen-contenedor--zoom {
  cursor: grab;
}

.imagen-contenedor--zoom:active {
  cursor: grabbing;
}

.imagen-viewer {
  max-width: 100%;
  max-height: 80vh;
  width: auto;
  height: auto;
  cursor: zoom-in;
  object-fit: contain;
  user-select: none;
  will-change: transform;
}

.imagen-viewer--zoom {
  cursor: inherit;
}

.video-viewer {
  width: 100%;
  max-height: 80vh;
  background: #ffffff;
}

.nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  background: rgba(255, 255, 255, 0.85) !important;
}

.nav-btn-left {
  left: 4px;
}

.nav-btn-right {
  right: 4px;
}

.zoom-slider {
  max-width: 160px;
  flex: 0 1 160px;
}

.zoom-porcentaje {
  min-width: 42px;
  text-align: right;
}
</style>
